import { ActivatedRoute, Router } from '@angular/router';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';

import { saveAs } from 'file-saver';
import { interval, of, Subscription } from 'rxjs';
import { DialogService } from 'primeng/dynamicdialog';
import { map, switchMap, take, tap } from 'rxjs/operators';

import { Portals } from '@qwyk/models';
import { PathService } from '@qwyk/core';
import { SimpleModalComponent } from '@qwyk/ui-standalone';
import { AuthenticationFacade } from '@qwyk/hub/authentication';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { QuotationsFacade } from '@qwyk/shared-stores/quotations';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { QuotationOffersFacade } from '@qwyk/hub/quotations-offers';

import { QuotationsService } from '../../services/quotations.service';

@Component({
    selector: 'qwyk-quotation-detail-page',
    templateUrl: './quotation-detail-page.component.html',
    styleUrls: ['./quotation-detail-page.component.scss'],
    providers: [DialogService],
})
export class QuotationDetailPageComponent implements OnInit, OnDestroy {

    private router = inject(Router);
    private pathService = inject(PathService);
    private modal = inject(DialogService);
    private route = inject(ActivatedRoute);
    private service = inject(QuotationsService);
    private quotations = inject(QuotationsFacade);
    private auth = inject(AuthenticationFacade);
    private quotationOffers = inject(QuotationOffersFacade);

    public busy: boolean;
    public isPolling = false;

    public url$ = this.pathService.path$;
    public selectedQuotation$ = this.quotations.selectedQuotations$;

    public isMasterAdmin$ = this.auth.user$.pipe(map(e => e.is_master_admin));
    private actionsSubscription: Subscription;
    private queryParamsSubscription: Subscription;

    ngOnInit(): void {
        this.actionsSubscription = this.route.params.subscribe(params => {
            this.quotations.selectQuotation(params.id);
            this.queryParamsSubscription = this.route.queryParamMap
                .pipe(
                    switchMap(query => {
                        if (query.has('poll') && query.get('poll') === 'true') {
                            this.isPolling = true;
                            return interval(5 * 1e3);
                        } else {
                            return of(false);
                        }
                    })
                )
                .subscribe(query => {
                    if (query !== false) {
                        const POLLING_QUERY_THRESHOLD = (120 * 1e3) / (5 * 1e3); // Or simply 24

                        if ((query as number) > POLLING_QUERY_THRESHOLD && this.isPolling) {
                            this.isPolling = false;
                            this.router.navigate(['.'], {
                                relativeTo: this.route,
                            });
                        } else {
                            this.isPolling = true;
                            this.quotations.loadQuotation(params.id);
                        }
                    } else {
                        this.isPolling = false;
                    }
                });
        });
    }

    ngOnDestroy() {
        this.actionsSubscription.unsubscribe();
        this.queryParamsSubscription.unsubscribe();
        this.quotationOffers.reset();
        this.quotations.resetSelectedQuotation();
    }

    download(quotation: Portals.QuotationEntity) {
        this.busy = true;
        const subscription = this.service
            .downloadQuotation(quotation.id)
            .pipe(take(1))
            .subscribe(
                response => {
                    subscription.unsubscribe();
                    saveAs(response, `quotation-${quotation.id}.pdf`);
                    this.busy = false;
                },
                () => {
                    subscription.unsubscribe();
                    this.busy = false;
                    alert(
                        'Something went wrong, please try again or contact support if the problem persists.'
                    );
                }
            );
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any
    publish(quotation: Portals.QuotationEntity, _modal: any) {
        const modalRef = this.modal.open(SimpleModalComponent, {
            position: 'center',
            showHeader: false,
            closable: true,
            width: '50%',
            contentStyle: {
                'padding': 0
            },
            data: {
                header: 'Publish quotation',
                message:
                    'This will publish the quotation and make it visible to your customer in their portal. Do you wish to continue?',
                dismissAction: 'No',
                confirmAction: 'Yes',
            },
        });

        modalRef.onClose.subscribe((result: boolean) => {
            if (result) {
                this.busy = true;
                const subscription = this.service
                    .publishQuotation(quotation)
                    .pipe(
                        take(1),
                        tap(updateQuotation =>
                            this.quotations.quotationUpdated(updateQuotation)
                        )
                    )
                    .subscribe(
                        () => {
                            this.busy = false;
                            subscription.unsubscribe();
                        },
                        () => {
                            this.busy = false;
                            subscription.unsubscribe();
                            alert(
                                'Something went wrong, please try again or contact support if the problem persists.'
                            );
                        }
                    );
            }
        });
    }

    close(quotation: Portals.QuotationEntity) {
        this.busy = true;
        this.service
            .closeQuotation(quotation)
            .pipe(
                take(1),
                tap(updatedQuotation =>
                    this.quotations.quotationUpdated(updatedQuotation)
                )
            )
            .subscribe(
                () => {
                    this.busy = false;
                },
                () => {
                    this.busy = false;
                    alert(
                        'Something went wrong, please try again or contact support if the problem persists.'
                    );
                }
            );
    }

    delete(quotation: Portals.QuotationEntity) {
        const modalRef = this.modal.open(SimpleModalComponent, {
            position: 'center',
            showHeader: false,
            closable: true,
            width: '50%',
            contentStyle: {
                'padding': 0
            },
            data: {
                header: 'Delete quotation',
                message:
                    'Are you sure you want to delete this quotation? This cannot be undone.',
                dismissAction: 'No',
                confirmAction: 'Yes',
                confirmStyle: 'btn-danger',
            },
        });

        modalRef.onClose.subscribe(result => {
            if (result) {
                this.busy = true;
                const subscription = this.service
                    .deleteQuotation(quotation)
                    .pipe(take(1))
                    .subscribe(
                        () => {
                            subscription.unsubscribe();
                            this.router.navigate(['..'], {
                                relativeTo: this.route,
                            });
                        },
                        () => {
                            this.busy = false;
                            subscription.unsubscribe();
                            alert(
                                'Something went wrong, please try again or contact support if the problem persists.'
                            );
                        }
                    );
                }
        });
    }
}
